import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { type FC, useContext } from "react";
import { Collection, SubmenuTrigger } from "react-aria-components";
import { FilterContext } from "./filterContext.js";
import type { FilterParams } from "./filterParams.js";
import type { SignalMetadataCondition } from "./signalMetadataCondition.js";
import {
  renderAddableMetadataField,
  useAddableMetadataFields,
} from "./signalMetadataFieldMenu.js";

export interface SignalMetadataFilterOptionProps {
  params: FilterParams;
  value: readonly SignalMetadataCondition[];
  onChange: (newValue: readonly SignalMetadataCondition[]) => void;
}

/**
 * "+ Add filter" menu entry for signal metadata fields, shown once a signal is
 * selected via the `signalId` filter. The drilldown page pins its signal via
 * `params.signalId` and surfaces metadata as an always-visible chip instead, so
 * this option hides itself there.
 */
export const SignalMetadataFilterOption: FC<
  SignalMetadataFilterOptionProps
> = ({ params, value, onChange }) => {
  const { values } = useContext(FilterContext);
  const signalId =
    params.signalId != null ? undefined : (values.signalId ?? undefined);

  const addableFields = useAddableMetadataFields(
    signalId,
    params.playbookId,
    value,
  );

  if (!signalId || !params.playbookId || addableFields.length === 0) {
    return null;
  }

  return (
    <SubmenuTrigger>
      <SimpleMenuItem>Signal fields</SimpleMenuItem>

      <SimpleMenuPopover>
        <SimpleMenu aria-label="Filter by signal field">
          <Collection items={addableFields}>
            {(field) => renderAddableMetadataField(field, value, onChange)}
          </Collection>
        </SimpleMenu>
      </SimpleMenuPopover>
    </SubmenuTrigger>
  );
};
